import { useMutation } from '@tanstack/react-query';

import { useTranslations } from 'next-intl';
import {
  claimAllMissions,
  recruitRoster,
  updateRoster,
} from '@wwe-portal/web/services/client/club-store';
import { VERIFY_DURATION_MS } from '@wwe-portal/web/utils/constants/offer';
import {
  RecruitRosterRequest,
  UpdateRosterResponse,
  UpgradeRosterRequest,
} from '@wwe-portal/entities/club-store.type';
import { AxiosResponse } from 'axios';
import {
  useInvalidateClaimAllStatus,
  useInvalidateRosterItems,
} from './club-store';
import {
  isAPIError,
  useHandleCommonError,
} from '@wwe-portal/web/utils/error-handler/error';
import { errorCodes } from '@wwe-portal/web/utils/constants/error-code';

import { NotEnoughClubCoinDialog } from '@wwe-portal/web/components/club-store/modals/NotEnoughClubCoinDialog';

import { useInvalidateBalance } from '@wwe-portal/web/providers/queries/currency';
import { createPortableDialog } from '@wwe-portal/ui/components/dialog';
import { toast } from '@wwe-portal/ui/components/toast';
import { useProcessingDialog } from '@wwe-portal/web/hooks/payment/processing-dialog';

const wait = (ms: number) =>
  new Promise((r) => setTimeout(() => r(undefined), ms));

export const useMutateClaimAll = () => {
  const t = useTranslations();
  const onError = useHandleCommonError();
  const { showProcess, showSuccess, modal } = useProcessingDialog({
    progressData: {
      title: t('claimAll_title_load'),
      description: t('claimAll_description_load'),
      duration: 5000,
    },
    successData: {
      title: t('claimAll_title_successful'),
      description: t('claimAll_description_successful'),
    },
  });
  const invalidateClaimAllStatus = useInvalidateClaimAllStatus();
  const invalidateBalance = useInvalidateBalance();
  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      showProcess();
      return Promise.all([claimAllMissions(), wait(5000)]);
    },

    onSuccess: (data) => {
      if (!data[0].data.data.success) {
        toast({
          content: data[0].data.data.message,
          variant: 'error',
        });
        modal.remove();
      } else {
        showSuccess();
      }
      invalidateClaimAllStatus();
      invalidateBalance();
    },

    onError,
  });
  return {
    mutate,
    isPending,
  };
};

interface MutationCallback<V> {
  onSuccess?: (
    data: AxiosResponse<{ data: UpdateRosterResponse }>,
    variables: V
  ) => void;
  onError?: (error: unknown) => void;
  onCloseSuccessDialog?: () => void;
}

interface DialogContent {
  progressTitle: string;
  progressDecription: string;
  successTitle: string;
  successDescription: string;
}
export const useMutationTemplate = <V,>(
  callback: MutationCallback<V>,
  content: DialogContent
) => {
  const invalidate = useInvalidateRosterItems();
  const handleCommonError = useHandleCommonError();
  const t = useTranslations();
  const invalidateBalance = useInvalidateBalance();
  const { showProcess, showSuccess, modal } = useProcessingDialog({
    progressData: {
      title: t(content.progressTitle),
      description: t(content.progressDecription),
      duration: VERIFY_DURATION_MS,
    },
    successData: {
      title: t(content.successTitle),
      description: t(content.successDescription),
    },
    onClose: callback.onCloseSuccessDialog,
  });

  const showProgressingDialog = async () => {
    showProcess();
    await wait(VERIFY_DURATION_MS);
    invalidate();
    showSuccess();
  };

  const onSuccess: MutationCallback<V>['onSuccess'] = (data, variables) => {
    if (!data.data.data.success) {
      throw Error(data.data.data.message);
    }

    showProgressingDialog();
    invalidateBalance();
    return callback.onSuccess?.(data, variables);
  };
  const onError = (error: unknown) => {
    modal.remove();
    if (isAPIError(error, errorCodes.NOT_ENOUGH_CLUBCOIN)) {
      createPortableDialog(NotEnoughClubCoinDialog);
    } else {
      handleCommonError(error);
    }
    callback.onError?.(error);
  };

  return {
    onSuccess,
    onError,
  };
};

export const useMutateFuseUp = (
  callback: MutationCallback<UpgradeRosterRequest>
) => {
  const { onError, onSuccess } = useMutationTemplate(callback, {
    progressTitle: 'fuseUp_progress_title',
    progressDecription: 'fuseUp_progress_description',
    successTitle: 'fuseUp_success_title',
    successDescription: 'fuseUp_success_description',
  });

  return useMutation({
    mutationFn: (data: UpgradeRosterRequest) => {
      return updateRoster(data);
    },
    onSuccess: (data, variables) => {
      onSuccess(data, variables);
    },
    onError,
  });
};

export const useMutateRecruit = (
  callback: MutationCallback<RecruitRosterRequest>
) => {
  const { onError, onSuccess } = useMutationTemplate(callback, {
    progressTitle: 'recruitment_progress_title',
    progressDecription: 'recruitment_progress_description',
    successTitle: 'recruitment_success_title',
    successDescription: 'recruitment_success_description',
  });

  return useMutation({
    mutationFn: async (data: RecruitRosterRequest) => {
      return await recruitRoster(data);
    },
    onSuccess: (data, variables) => onSuccess(data, variables),
    onError,
  });
};
